/**
 * <s-rotator> — shows one message at a time from a short list and moves to
 * the next every few seconds.
 *
 * Markup contract:
 * - [data-rotator-list]   wrapper of the messages
 * - [data-rotator-item]   each message (two or more)
 * - [data-rotator-toggle] optional visible pause/play button
 * - attribute `interval`  milliseconds between messages (default 4000)
 * - attributes `label-pause` / `label-play` for the button's accessible name
 *
 * Accessibility:
 * - Visible pause/play button (WCAG 2.2.2); an explicit pause is sticky.
 * - Pauses on hover over the messages and while the tab is hidden.
 * - Never autoplays when the visitor prefers reduced motion.
 * - Messages are aria-live="off" while rotating and "polite" when paused.
 * - Without JavaScript every message stays visible in a simple list.
 */
class SRotator extends HTMLElement {
  #ac;
  #timer = 0;
  #index = 0;
  #items = [];
  #list;
  #button;
  #playing = true;
  #hovering = false;
  #docHidden = false;

  connectedCallback() {
    this.#items = [...this.querySelectorAll('[data-rotator-item]')];
    this.#list = this.querySelector('[data-rotator-list]');
    this.#button = this.querySelector('[data-rotator-toggle]');
    if (this.#items.length < 2 || !this.#list) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) this.#playing = false;
    this.#docHidden = document.hidden;

    this.#list.addEventListener('pointerenter', () => { this.#hovering = true; this.#sync(); }, opts);
    this.#list.addEventListener('pointerleave', () => { this.#hovering = false; this.#sync(); }, opts);
    document.addEventListener('visibilitychange', () => { this.#docHidden = document.hidden; this.#sync(); }, opts);
    this.#button?.addEventListener('click', () => { this.#playing = !this.#playing; this.#sync(); }, opts);

    // Optional `media` attribute: rotate only while this media query matches;
    // otherwise every message stays visible (e.g. a desktop row).
    const media = this.getAttribute('media');
    if (media) {
      const mql = window.matchMedia(media);
      mql.addEventListener('change', () => this.#applyMedia(mql.matches), opts);
      this.#applyMedia(mql.matches);
      return;
    }

    this.#start();
  }

  #start() {
    this.#show(this.#index);
    this.dataset.ready = '';
    this.#sync();
  }

  #applyMedia(matches) {
    if (matches) {
      this.#start();
      return;
    }
    clearInterval(this.#timer);
    this.#timer = 0;
    delete this.dataset.ready;
    this.#list.removeAttribute('aria-live');
    this.#items.forEach((item) => {
      item.removeAttribute('data-active');
      item.removeAttribute('aria-hidden');
    });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearInterval(this.#timer);
    this.#timer = 0;
  }

  get #interval() {
    const value = Number.parseInt(this.getAttribute('interval'), 10);
    return Number.isFinite(value) && value >= 2000 ? value : 4000;
  }

  #sync() {
    clearInterval(this.#timer);
    this.#timer = 0;
    if (!('ready' in this.dataset)) return;
    const running = this.#playing && !this.#hovering && !this.#docHidden;
    this.#timer = running ? setInterval(() => this.#show(this.#index + 1), this.#interval) : 0;

    this.#list.setAttribute('aria-live', running ? 'off' : 'polite');
    this.toggleAttribute('data-playing', this.#playing);
    this.#button?.setAttribute('aria-label', this.getAttribute(this.#playing ? 'label-pause' : 'label-play') || '');
  }

  #show(next) {
    const count = this.#items.length;
    this.#index = ((next % count) + count) % count;
    this.#items.forEach((item, n) => {
      const active = n === this.#index;
      item.toggleAttribute('data-active', active);
      item.setAttribute('aria-hidden', String(!active));
    });
  }
}

if (!customElements.get('s-rotator')) customElements.define('s-rotator', SRotator);
